import { createFileRoute, Link } from "@tanstack/react-router"
import {
  ArrowRight,
  ClipboardList,
  Plus,
  Upload,
  UserPlus,
  Users,
} from "lucide-react"

import {
  buttonClasses,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ErrorState,
  PageHeader,
  Skeleton,
} from "@/components/ui"
import { useSummary } from "@/features/reports/api"
import { DashboardFigures, LibraryFigures } from "@/features/reports/dashboard-figures"
import { displayName, useSession } from "@/lib/session"

export const Route = createFileRoute("/")({ component: Dashboard })

function greetingFor(hour: number) {
  if (hour < 12) return "Good morning"
  if (hour < 17) return "Good afternoon"
  return "Good evening"
}

function Dashboard() {
  const { me, canWrite, isAdministrator } = useSession()
  const summary = useSummary()
  const data = summary.data

  return (
    <>
      {/* The dashboard wears the same banner as every other page (Dawid,
          2026-08-01), and the band says "Dashboard" like the sidebar and the
          approved mock (Dawid, 2026-08-04): a band title matches its own nav
          item everywhere. The greeting stays as the lead line of the
          content, which is what it always was. */}
      <PageHeader title="Dashboard" />
      {/* Branded hero. The wash is drawn from the brand tokens (the CI's
          organic graphic language), not a photograph: the CI's photographic
          style asks for the client's own operational photography, which is
          instance content, not something the product hardcodes. */}
      {/* `card-surface` rather than a hand-rolled border, radius and fill: the
          hero had drifted to a 27px radius and no shadow while every card on
          the screen sits at 19px with `shadow-card`, which is the radius
          inconsistency reported from the client meeting. One surface
          definition is what stops a panel and a card drifting apart. */}
      <section className="card-surface brand-flow rise-in relative mb-6 overflow-hidden">
        <div className="relative flex flex-col gap-6 p-6 sm:p-8 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0">
            {/* An h2: the band above owns the page's h1. */}
            <h2 className="text-3xl sm:text-4xl">
              {me
                ? `${greetingFor(new Date().getHours())}, ${displayName(me).split(" ")[0]}`
                : "Dashboard"}
            </h2>
            {data ? (
              <p className="mt-3 max-w-xl text-base leading-relaxed text-foreground/75">
                {data.events_today > 0
                  ? `${data.events_today} training ${data.events_today === 1 ? "event runs" : "events run"} today, `
                  : "No training runs today, "}
                with{" "}
                <span className="font-semibold text-foreground">
                  {data.upcoming_events}
                </span>{" "}
                {data.upcoming_events === 1 ? "event" : "events"} scheduled ahead
                and{" "}
                <span className="font-semibold text-foreground">
                  {data.records}
                </span>{" "}
                learner records on the book.
              </p>
            ) : (
              <p className="mt-3 max-w-xl text-sm text-muted-foreground">
                Where training stands today, and what needs attention next.
              </p>
            )}
          </div>
          <div className="flex w-full flex-wrap items-center gap-2.5 lg:w-auto lg:shrink-0">
            {canWrite && (
              <Link to="/employees" className={buttonClasses({ variant: "secondary" })}>
                <UserPlus className="h-4 w-4" aria-hidden /> Add employee
              </Link>
            )}
            {canWrite && (
              <Link to="/events/new" className={buttonClasses({ variant: "primary" })}>
                <Plus className="h-4 w-4" aria-hidden /> New training event
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* The figures arrive after the page does, and the swap from skeletons to
          numbers is a purely visual event: nothing announces that the dashboard
          now has content. One polite line does, and only on the transition,
          because the string is stable afterwards and a live region announces
          changes rather than presence. A refetch does not re-announce, since
          `isLoading` is false for everything but the first load. */}
      <p aria-live="polite" className="sr-only">
        {summary.isError
          ? "The dashboard figures could not be loaded."
          : summary.isLoading || !data
            ? ""
            : "Dashboard figures loaded."}
      </p>

      {summary.isError ? (
        <ErrorState
          title="The dashboard could not be loaded"
          description="Every figure here is read fresh, so nothing is shown rather than a stale or invented number."
          onRetry={() => void summary.refetch()}
        />
      ) : summary.isLoading || !data ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {/* The placeholder wears the radius of the card it stands in for.
              `Skeleton`'s own `rounded` is 4px, so the loading state was a
              different shape from the thing that replaced it. */}
          <Skeleton className="h-64 rounded-xl lg:row-span-2" />
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-28 rounded-xl" />
          ))}
        </div>
      ) : (
        <DashboardFigures data={data} />
      )}

      {/* Quick actions runs the full width now.
          -------------------------------------------------------------------
          It used to be the narrow third of a row whose other two thirds held
          the training-currency explanation, and that explanation has moved
          onto the card that states the figure (worklist item 13). What was
          left was a tall card with nothing beside it, so the row was rebuilt
          rather than left with a hole in it: the actions lay out two abreast
          from `sm`, which keeps every label on one line at the width they
          then have, and the card is as tall as its contents rather than as
          tall as whatever used to sit next to it. A Viewer sees two actions
          and a one-row card; nobody sees empty space. */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Quick actions</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-2.5 sm:grid-cols-2">
          {canWrite && (
            <QuickAction to="/events/new" icon={Plus} label="Create a training event" />
          )}
          {isAdministrator && (
            <QuickAction to="/imports" icon={Upload} label="Import employee data" />
          )}
          <QuickAction
            to="/records"
            icon={ClipboardList}
            label="Review training records"
          />
          <QuickAction to="/employees" icon={Users} label="Browse employees" />
        </CardContent>
      </Card>

      {data && (
        <div className="mt-6">
          <LibraryFigures data={data} />
        </div>
      )}
    </>
  )
}

function QuickAction({
  to,
  icon: Icon,
  label,
}: {
  to: string
  icon: typeof Plus
  label: string
}) {
  // `rounded-lg` (15px) inside the 19px card that holds it, rather than the
  // 19px it wore before: a nested surface at exactly its parent's radius reads
  // as a mis-nesting, and 15px is the same small-element radius the icon chips
  // and the buttons carry.
  return (
    <Link
      to={to}
      className="group flex items-center justify-between rounded-lg border border-border bg-card px-3.5 py-3 text-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/25 hover:bg-primary/[0.04] hover:shadow-soft"
    >
      <span className="flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-lg bg-primary/10 text-primary transition-colors group-hover:bg-primary/15">
          <Icon className="h-5 w-5" aria-hidden />
        </span>
        <span className="font-medium text-foreground">{label}</span>
      </span>
      <ArrowRight
        className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5"
        aria-hidden
      />
    </Link>
  )
}
